import {Injectable} from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import {catchError, concat, forkJoin, Observable, of, switchMap, take, tap} from 'rxjs';
import {NavigationService} from 'app/core/navigation/navigation.service';
import {ShortcutsService} from 'app/layout/common/shortcuts/shortcuts.service';
import {UserService} from 'app/core/user/user.service';
import {AuthService} from "./core/auth/auth.service";
import {UrlEntryService} from "./core/urlEntry/url-entry.service";
import {AppModeService} from '@store/services/app-mode.service'
import {AppMode} from "./enums/AppModeEnum";
import {PublicationAccessService} from "./modules/landing-publication/publication-access.service";
import {TestSurveyService} from "../@fuse/components/test-survey/test-survey.service";
import {ExtProfilesService} from "./modules/landing-publication/ext-profiles/ext-profiles.service";
import {LangsListParamsService} from "@store/langs-list-params.service";

@Injectable({
  providedIn: 'root'
})
export class InitialDataResolver  {
  constructor(
    private _navigationService: NavigationService,
    private _shortcutsService: ShortcutsService,
    private _userService: UserService,
    private _authService: AuthService,
    private urlEntryService: UrlEntryService,
    private _router: Router,
    private appModeService: AppModeService,
    private publicationAccessService: PublicationAccessService,
    private testSurveyService: TestSurveyService,
    private extProfilesService: ExtProfilesService,
    private langsListParamsService: LangsListParamsService,
  ) {

  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Use this resolver to resolve initial mock-api for the application
   *
   * @param route
   * @param state
   */
  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {

    //
    // Pour attraper tout de suite le mode 'preview' et activer le test mode.
    // if the url contains 'survey/preview' we activate the testSurveyMode
    const fullUrl = state.url;
    if (fullUrl.includes('survey/preview')) {
      this.appModeService.appMode$.next(AppMode.PREVIEW);
    }

    this.appModeService.switchLangFromUrl(route.queryParamMap); // <--- Redétecté dans le preview resolver pour pouvoir switcher comme il faut les langues.
    // const lang = route.queryParamMap.get('lang');
    // if (lang) {
    //   this.appModeService.switchLang(lang); //
    // }
    const isDebug = route.queryParamMap.get('debug');
    if (isDebug && isDebug === '123') {
      this.appModeService.isDebug$.next(true)
    }
    const testSurvey = route.queryParamMap.get('testSurvey');
    if (testSurvey && testSurvey === '1') {
      this.testSurveyService.activateTestSurveyMode();
    }
    //
    //
    //

    this.urlEntryService.storeUrlInfos(route.queryParams);

    const askLang = route.queryParamMap.get('ask_lang') || route.paramMap.get('ask_lang') || false;
    this.langsListParamsService.setShouldAskLang(askLang === '1');

    const jwtToken = route.queryParamMap.get('jwtToken');
    const surveyId = parseInt(route.params['surveyId'] as string);
    const pubUuid = route.queryParamMap.get('pubUuid') || route.paramMap.get('pubUuid');

    const comId = route.queryParamMap.get('com_ID') || this.publicationAccessService.comId || null;

    // Parent resolve s'exécute avant PublicationResolver : retirer un JWT périmé pour les liens publics
    // afin que getPublicationInfos ne parte pas avec un Bearer invalide (401 + boucle de reload).
    if (state.url.includes('/publication/public')) {
      localStorage.removeItem('accessToken');
    }

    let signInObservable = of(true);
    if (jwtToken) {
      this._authService.accessToken = jwtToken;
      signInObservable = this._authService.signInUsingToken(jwtToken)
        .pipe(
          take(1),
          switchMap(logged => {
            if (!logged) {
              return of(false);
            }
            return of(true);
          })
        );
    }

    let metasInfosObservable = of(true);
    if (pubUuid && this.publicationAccessService.publication$.value.pubUuid !== pubUuid) {
      metasInfosObservable = this.publicationAccessService
        .doGetMetasInfos(pubUuid, parseInt(comId as string))
        .pipe(
          take(1),
          switchMap((responseData) => {
            if (!responseData && this.appModeService.appMode$.value === AppMode.PREVIEW) {

              this.extProfilesService.setAll([]);
              // this.extProfilesService.set({
              //   extDef: {},
              //   ask: [],
              //   neverAsk: [],
              //   randomAssign: []
              // });
              return of(true);

            }
            
            // Vérifier que responseData existe et a une propriété data
            if (!responseData || !responseData.data) {
              // En cas d'erreur (responseData = false), initialiser avec des valeurs par défaut
              this.extProfilesService.setAll([]);
              this.extProfilesService.randomAssign$.next([]);
              return of(true);
            }
            
            // On initialise le service de ExtProfils (fait 'normalement' dans un effet -> CurrentPublicationEffects
            this.extProfilesService.setAll(responseData.data?.extProfilsNeeded?.all);
            this.extProfilesService.randomAssign$.next(responseData.data?.extProfilsNeeded?.randomAssign || []);
            this.extProfilesService.set(responseData.data?.extProfilsNeeded);

            return of(true);

          }),
          catchError((error) => {
            // Gérer les erreurs qui pourraient survenir dans le switchMap
            console.error('[InitialDataResolver] Error in metasInfosObservable:', error);
            this.extProfilesService.setAll([]);
            this.extProfilesService.randomAssign$.next([]);
            return of(true);
          }),
        );

    }

    return forkJoin([signInObservable, metasInfosObservable])
      .pipe(
        switchMap(results => {

          if (results.includes(false)) {
            return of(false);
          }

          return this.handleRoute(route, state, surveyId);

        })
      );

    // return concat(metasInfosObservable, signInObservable).pipe(
    //   switchMap(result => {
    //     if (!result) {
    //       return of(false);
    //     }
    //     return this.handleRoute(route, state, surveyId);
    //   })
    // );

  }

  private handleRoute(route: ActivatedRouteSnapshot, state: RouterStateSnapshot, surveyId: number): Observable<any> {

    switch (state.url) {
      case '/home':
        this.appModeService.appMode$.next(AppMode.HOME)
        if (this._userService.user$.value.isPublic) {
          return forkJoin(
            [this._router.navigate(['/sign-out'])],
          )
        }
        break;

      default:

    }

    // On verra plus tard ce que l'on fait avec ça....
    //
    // Fork join multiple API endpoint calls to wait all of them to finish
    return forkJoin([
      this._navigationService.get(),
      this._shortcutsService.getAll(),
    ]);

  }

}
